import { useEffect, useRef, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import {
  DEFAULT_CHECK_WINDOW_MINUTES,
  HealthCheck,
  buildQuery,
  searchMetrics,
} from './api';

const AGGREGATIONS = ['avg', 'sum', 'max', 'min'];

// Evaluation windows for metric checks ("sum of errors over the last ...").
const WINDOWS: { minutes: number; label: string }[] = [
  { minutes: 5, label: '5 min' },
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 1440, label: '24 hours' },
];

const useStyles = makeStyles(theme => ({
  section: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    margin: theme.spacing(2, 0, 1),
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
  },
  checkName: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  checkDetail: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: theme.palette.text.secondary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  field: {
    marginBottom: theme.spacing(1.5),
  },
  fieldRow: {
    display: 'flex',
    gap: 8,
    marginBottom: theme.spacing(1.5),
  },
  metricWrapper: {
    position: 'relative',
    marginBottom: theme.spacing(1.5),
  },
  suggestions: {
    position: 'absolute',
    zIndex: theme.zIndex.modal + 1,
    left: 0,
    right: 0,
    maxHeight: 200,
    overflowY: 'auto',
  },
  suggestionText: {
    fontFamily: 'monospace',
    fontSize: 13,
  },
  empty: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

/**
 * Manages the entity's shared health checks: server-side pings and Datadog
 * metric thresholds shown as health tiles on Overview and Observability.
 */
export function HealthDialog({
  open,
  initialChecks,
  metricSeed,
  onClose,
  onSave,
}: {
  open: boolean;
  initialChecks: HealthCheck[];
  /** Pre-filled metric search term, e.g. `savr.my_service`. */
  metricSeed?: string;
  onClose: () => void;
  onSave: (checks: HealthCheck[]) => void;
}) {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [checks, setChecks] = useState<HealthCheck[]>(initialChecks);
  // Index of the check currently loaded into the form, or null when adding.
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [kind, setKind] = useState<'ping' | 'metric'>('ping');
  const [name, setName] = useState('');

  // Ping fields.
  const [url, setUrl] = useState('');

  // Metric fields.
  const [metric, setMetric] = useState(metricSeed ?? '');
  const [aggregation, setAggregation] = useState('avg');
  const [windowMinutes, setWindowMinutes] = useState(
    DEFAULT_CHECK_WINDOW_MINUTES,
  );
  const [noData, setNoData] = useState<'unknown' | 'healthy' | 'unhealthy'>(
    'unknown',
  );
  const [filter, setFilter] = useState('');
  const [alarmWhen, setAlarmWhen] = useState<'above' | 'below'>('above');
  const [warning, setWarning] = useState('');
  const [critical, setCritical] = useState('');
  const [unit, setUnit] = useState('');

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suppressSearch = useRef(false);

  useEffect(() => {
    if (kind !== 'metric' || metric.trim().length < 2) {
      setSuggestions([]);
      return undefined;
    }
    if (suppressSearch.current) {
      suppressSearch.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      searchMetrics({ discoveryApi, fetchApi }, metric.trim())
        .then(result => setSuggestions(result.slice(0, 25)))
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [metric, kind, discoveryApi, fetchApi]);

  const addValid =
    kind === 'ping'
      ? Boolean(url.trim()) &&
        (url.trim().startsWith('http://') || url.trim().startsWith('https://'))
      : Boolean(metric.trim()) &&
        critical.trim() !== '' &&
        !Number.isNaN(Number(critical));

  const clearForm = () => {
    setEditIndex(null);
    setName('');
    setUrl('');
    setMetric('');
    setWarning('');
    setCritical('');
    setUnit('');
    setWindowMinutes(DEFAULT_CHECK_WINDOW_MINUTES);
    setNoData('unknown');
  };

  // Loads an existing check into the form so it can be saved back in place.
  const startEdit = (index: number) => {
    const check = checks[index];
    setEditIndex(index);
    setKind(check.type);
    setName(check.name);
    if (check.type === 'ping') {
      setUrl(check.url);
    } else {
      suppressSearch.current = true;
      setMetric(check.metrics?.[0] ?? '');
      setAggregation(check.aggregation ?? 'avg');
      setWindowMinutes(check.windowMinutes ?? DEFAULT_CHECK_WINDOW_MINUTES);
      setNoData(check.noData ?? 'unknown');
      setFilter(check.filter ?? '');
      setAlarmWhen(check.alarmWhen);
      setWarning(check.warning !== undefined ? String(check.warning) : '');
      setCritical(String(check.critical));
      setUnit(check.unit ?? '');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const submitCheck = () => {
    let check: HealthCheck;
    if (kind === 'ping') {
      check = { type: 'ping', name: name.trim() || 'Ping', url: url.trim() };
    } else {
      const metricName = metric.trim();
      check = {
        type: 'metric',
        name: name.trim() || metricName,
        query: buildQuery({ metrics: [metricName], aggregation, filter }),
        alarmWhen,
        critical: Number(critical),
        warning: warning.trim() === '' ? undefined : Number(warning),
        unit: unit.trim() || undefined,
        windowMinutes,
        noData: noData === 'unknown' ? undefined : noData,
        metrics: [metricName],
        aggregation,
        filter: filter.trim() || undefined,
      };
    }
    setChecks(
      editIndex === null
        ? [...checks, check]
        : checks.map((existing, i) => (i === editIndex ? check : existing)),
    );
    clearForm();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Health checks</DialogTitle>
      <DialogContent>
        <Typography className={classes.section}>Current checks</Typography>
        {checks.length === 0 && (
          <Typography className={classes.empty}>
            None yet - pings show up/down + latency, metric checks turn a
            Datadog query into a healthy/warning/unhealthy tile.
          </Typography>
        )}
        {checks.map((check, index) => (
          <div key={`${check.name}-${index}`} className={classes.checkRow}>
            <span className={classes.checkName}>{check.name}</span>
            <span className={classes.checkDetail}>
              {check.type === 'ping'
                ? check.url
                : `${check.query} · unhealthy ${check.alarmWhen} ${
                    check.critical
                  } over ${
                    check.windowMinutes ?? DEFAULT_CHECK_WINDOW_MINUTES
                  }m`}
            </span>
            <IconButton
              size="small"
              title="Edit check"
              onClick={() => startEdit(index)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Remove check"
              onClick={() => {
                setChecks(checks.filter((_, i) => i !== index));
                if (editIndex === index) {
                  clearForm();
                } else if (editIndex !== null && index < editIndex) {
                  setEditIndex(editIndex - 1);
                }
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </div>
        ))}

        <Typography className={classes.section}>
          {editIndex === null
            ? 'Add a check'
            : `Edit "${checks[editIndex]?.name}"`}
        </Typography>
        <div className={classes.fieldRow}>
          <FormControl variant="outlined" size="small" style={{ width: 130 }}>
            <InputLabel id="health-kind-label">Type</InputLabel>
            <Select
              labelId="health-kind-label"
              label="Type"
              value={kind}
              onChange={event =>
                setKind(event.target.value as 'ping' | 'metric')
              }
            >
              <MenuItem value="ping">Ping</MenuItem>
              <MenuItem value="metric">Metric</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Name (optional)"
            value={name}
            onChange={event => setName(event.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
        </div>
        {kind === 'ping' ? (
          <TextField
            className={classes.field}
            label="URL"
            placeholder="https://my-service.api.savr.com/ping"
            value={url}
            onChange={event => setUrl(event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Pinged from the backend every minute - healthy on any 2xx"
            InputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        ) : (
          <>
            <div className={classes.metricWrapper}>
              <TextField
                label="Metric"
                placeholder="Start typing to search metric names"
                value={metric}
                onChange={event => {
                  setMetric(event.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  style: { fontFamily: 'monospace', fontSize: 13 },
                }}
              />
              {showSuggestions && suggestions.length > 0 && (
                <Paper className={classes.suggestions} elevation={4}>
                  <List dense disablePadding>
                    {suggestions.map(suggestion => (
                      <ListItem
                        key={suggestion}
                        button
                        onClick={() => {
                          suppressSearch.current = true;
                          setMetric(suggestion);
                          setSuggestions([]);
                          setShowSuggestions(false);
                        }}
                      >
                        <ListItemText
                          primary={suggestion}
                          classes={{ primary: classes.suggestionText }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </div>
            <div className={classes.fieldRow}>
              <FormControl
                variant="outlined"
                size="small"
                style={{ width: 130, flexShrink: 0 }}
              >
                <InputLabel id="health-agg-label">Aggregation</InputLabel>
                <Select
                  labelId="health-agg-label"
                  label="Aggregation"
                  value={aggregation}
                  onChange={event =>
                    setAggregation(event.target.value as string)
                  }
                >
                  {AGGREGATIONS.map(agg => (
                    <MenuItem key={agg} value={agg}>
                      {agg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                style={{ width: 140, flexShrink: 0 }}
              >
                <InputLabel id="health-window-label">Over the last</InputLabel>
                <Select
                  labelId="health-window-label"
                  label="Over the last"
                  value={windowMinutes}
                  onChange={event =>
                    setWindowMinutes(event.target.value as number)
                  }
                >
                  {WINDOWS.map(window => (
                    <MenuItem key={window.minutes} value={window.minutes}>
                      {window.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Filter tags (optional)"
                placeholder="env:production"
                value={filter}
                onChange={event => setFilter(event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControl
                variant="outlined"
                size="small"
                style={{ width: 190, flexShrink: 0 }}
              >
                <InputLabel id="health-alarm-label">Unhealthy when</InputLabel>
                <Select
                  labelId="health-alarm-label"
                  label="Unhealthy when"
                  value={alarmWhen}
                  onChange={event =>
                    setAlarmWhen(event.target.value as 'above' | 'below')
                  }
                >
                  <MenuItem value="above">Value is above</MenuItem>
                  <MenuItem value="below">Value is below</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Warning at"
                value={warning}
                onChange={event => setWarning(event.target.value)}
                variant="outlined"
                size="small"
                type="number"
              />
              <TextField
                label="Critical at"
                value={critical}
                onChange={event => setCritical(event.target.value)}
                variant="outlined"
                size="small"
                type="number"
                required
              />
              <TextField
                label="Unit"
                placeholder="ms"
                value={unit}
                onChange={event => setUnit(event.target.value)}
                variant="outlined"
                size="small"
                style={{ width: 90, flexShrink: 0 }}
              />
            </div>
            <div className={classes.fieldRow}>
              <FormControl
                variant="outlined"
                size="small"
                style={{ width: 260, flexShrink: 0 }}
              >
                <InputLabel id="health-nodata-label">
                  If there is no data
                </InputLabel>
                <Select
                  labelId="health-nodata-label"
                  label="If there is no data"
                  value={noData}
                  onChange={event =>
                    setNoData(
                      event.target.value as 'unknown' | 'healthy' | 'unhealthy',
                    )
                  }
                >
                  <MenuItem value="unknown">Ignore (show as unknown)</MenuItem>
                  <MenuItem value="healthy">Healthy - silence is good</MenuItem>
                  <MenuItem value="unhealthy">
                    Unhealthy - data expected
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </>
        )}
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={!addValid}
          onClick={submitCheck}
        >
          {editIndex === null ? 'Add check' : 'Update check'}
        </Button>
        {editIndex !== null && (
          <Button size="small" style={{ marginLeft: 8 }} onClick={clearForm}>
            Cancel edit
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSave(checks)}
          color="primary"
          variant="contained"
          disableElevation
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
